import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartBarIcon, 
  BuildingOfficeIcon,
  QueueListIcon,
  BeakerIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ScaleIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useAuth } from '../../contexts/AuthContext';
import { getOnboardingChecklist } from '../../hooks/useApi';
import apiClient from '../../utils/api';
import { OnboardingChecklistResponse } from '../../types/api';

interface GoatDashboardStats {
  total_goats: number;
  total_sheds: number;
  health_logs_count: number;
  breeding_records_count: number;
  active_goats: number;
  total_kidding_records: number;
}

const GoatDashboard: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { user } = useAuth();
  const [stats, setStats] = useState<GoatDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [checklist, setChecklist] = useState<OnboardingChecklistResponse | null>(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistError, setChecklistError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      if (!selectedLocation?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = await apiClient.get<GoatDashboardStats>(
          `/goat/dashboard/${selectedLocation.id}`
        );
        setStats(response.data);
      } catch (err: any) {
        console.error('Error fetching dashboard stats:', err);
        setError(err.response?.data?.detail || 'Failed to load dashboard statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [selectedLocation?.id]);

  useEffect(() => {
    const loadChecklist = async () => {
      if (!selectedLocation?.id) {
        setChecklist(null);
        return;
      }
      try {
        setChecklistLoading(true);
        setChecklistError('');
        const data = await getOnboardingChecklist(selectedLocation.id, 'goat');
        setChecklist(data);
      } catch (err: any) {
        setChecklistError(err?.response?.data?.detail || err?.message || 'Failed to load onboarding checklist');
        setChecklist(null);
      } finally {
        setChecklistLoading(false);
      }
    };

    loadChecklist();
  }, [selectedLocation?.id]);

  const OnboardingChecklistCard: React.FC = () => {
    if (!selectedLocation) return null;

    if (checklistLoading) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Guided Checklist</h2>
          <div className="flex justify-center py-2">...</div>
        </div>
      );
    }

    if (!checklist || checklist.percent_complete >= 100) return null;

    const Item: React.FC<{ done: boolean; label: string; action?: React.ReactNode }> = ({ done, label, action }) => (
      <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-b-0">
        <div className="flex items-center gap-2">
          <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
            done ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
          }`}>
            {done ? '✓' : '!'}
          </span>
          <span className="text-sm text-gray-800">{label}</span>
        </div>
        {action}
      </div>
    );

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Guided Checklist</h2>
          <span className="text-sm font-medium text-gray-600">{checklist.percent_complete}% complete</span>
        </div>

        <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ width: `${Math.max(0, Math.min(100, checklist.percent_complete))}%` }}
          />
        </div>

        {checklistError && (
          <div className="mb-3 text-sm text-red-600">{checklistError}</div>
        )}

        <div className="space-y-0">
          <Item
            done={checklist.master_data_ready}
            label="Goat master data ready"
            action={!checklist.master_data_ready && user?.role === 'admin' ? <Link to="/admin/master-data" className="text-xs text-blue-600 hover:text-blue-800">Open</Link> : undefined}
          />
          <Item
            done={checklist.has_pond_or_shed}
            label="Add first shed"
            action={!checklist.has_pond_or_shed ? <Link to="/goat/sheds/new" className="text-xs text-blue-600 hover:text-blue-800">Open</Link> : undefined}
          />
          <Item
            done={checklist.has_first_feed_log}
            label="Add first feed log"
            action={!checklist.has_first_feed_log ? <Link to="/goat/data-entry/feed" className="text-xs text-blue-600 hover:text-blue-800">Open</Link> : undefined}
          />
          <Item
            done={checklist.has_first_expense}
            label="Add first expense"
            action={!checklist.has_first_expense ? <Link to="/goat/data-entry/expense" className="text-xs text-blue-600 hover:text-blue-800">Open</Link> : undefined}
          />
          <Item
            done={checklist.has_first_income}
            label="Add first income"
            action={!checklist.has_first_income ? <Link to="/goat/data-entry/income" className="text-xs text-blue-600 hover:text-blue-800">Open</Link> : undefined}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-4">
          <ChartBarIcon className="h-10 w-10 text-green-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Goat Farming Dashboard</h1>
        <p className="text-lg text-gray-600">
          {selectedLocation?.name || 'Overview of your goat farming operations'}
        </p>
      </div>

      <OnboardingChecklistCard />

      {/* Quick Stats */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 rounded-lg bg-green-600">
              <BuildingOfficeIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Goats</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? '...' : stats?.total_goats ?? '-'}
              </p>
              {stats && stats.active_goats > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {stats.active_goats} active
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 rounded-lg bg-blue-600">
              <BuildingOfficeIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Sheds</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? '...' : stats?.total_sheds ?? '-'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 rounded-lg bg-purple-600">
              <QueueListIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Health Logs</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? '...' : stats?.health_logs_count ?? '-'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 rounded-lg bg-orange-600">
              <ChartBarIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Breeding Records</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? '...' : stats?.breeding_records_count ?? '-'}
              </p>
              {stats && stats.total_kidding_records > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {stats.total_kidding_records} kiddings
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/goat/master"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BuildingOfficeIcon className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Manage Goat</p>
              <p className="text-sm text-gray-500">View and manage goat master data</p>
            </div>
          </Link>

          <Link
            to="/goat/feed-logs"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BeakerIcon className="h-8 w-8 text-amber-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Feed</p>
              <p className="text-sm text-gray-500">View and manage feed logs</p>
            </div>
          </Link>

          <Link
            to="/goat/expense-logs"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CurrencyDollarIcon className="h-8 w-8 text-red-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Expense</p>
              <p className="text-sm text-gray-500">View and manage expense logs</p>
            </div>
          </Link>

          <Link
            to="/goat/income-logs"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BanknotesIcon className="h-8 w-8 text-emerald-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Income</p>
              <p className="text-sm text-gray-500">View and manage income logs</p>
            </div>
          </Link>

          <Link
            to="/goat/weight-register"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ScaleIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Weight Register</p>
              <p className="text-sm text-gray-500">Track and record goat weight measurements</p>
            </div>
          </Link>

          <Link
            to="/goat/health-logs"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ShieldCheckIcon className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Vaccination</p>
              <p className="text-sm text-gray-500">View and manage vaccination records</p>
            </div>
          </Link>

          <Link
            to="/goat/breeding-logs"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ChartBarIcon className="h-8 w-8 text-orange-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Breeding Log</p>
              <p className="text-sm text-gray-500">View breeding records</p>
            </div>
          </Link>

          <Link
            to="/goat/kidding-logs"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ChartBarIcon className="h-8 w-8 text-pink-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Kidding Log</p>
              <p className="text-sm text-gray-500">View kidding records</p>
            </div>
          </Link>
        </div>
      </div>

    </div>
  );
};

export default GoatDashboard;
